import { useEffect, useState } from "react";
import { Heart, UtensilsCrossed } from "lucide-react";
import { message } from "antd";
import favoriteMealService, { type FavoriteMeal } from "../services/FavoriteMealService";
import { getLocalDateString, getSuggestedMealContext } from "../utils/homePageUtils";

interface FavoriteMealsPanelProps {
  onLogged: () => void;
}

const FavoriteMealsPanel = ({ onLogged }: FavoriteMealsPanelProps) => {
  const [meals, setMeals] = useState<FavoriteMeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const suggested = getSuggestedMealContext().meal;

  useEffect(() => {
    let active = true;
    favoriteMealService
      .list()
      .then((items) => {
        if (active) setMeals(items);
      })
      .catch(() => {
        if (active) setError("We couldn't open your favourites. Check your connection and try again.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const logMeal = async (meal: FavoriteMeal) => {
    if (busyId) return;
    setBusyId(meal.favorite_meal_id);
    try {
      await favoriteMealService.logFromFavorite({
        favorite_meal_id: meal.favorite_meal_id,
        date: getLocalDateString(),
        meal_type: meal.meal_type || suggested,
      });
      message.success("Meal logged from favourites");
      onLogged();
    } catch {
      message.error("Couldn't log this favourite. Please try again.");
      setBusyId(null);
    }
  };

  const removeMeal = async (meal: FavoriteMeal) => {
    if (busyId) return;
    setBusyId(meal.favorite_meal_id);
    try {
      await favoriteMealService.unfavorite(meal.favorite_meal_id);
      setMeals((current) => current.filter((item) => item.favorite_meal_id !== meal.favorite_meal_id));
    } catch {
      message.error("Couldn't remove this favourite.");
    } finally {
      setBusyId(null);
    }
  };

  const repeat = meals.find(
    (meal) => meal.meal_type?.toLowerCase() === suggested.toLowerCase(),
  );

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center bg-neutral-950 px-6">
        <p className="text-subhead text-white/70">Loading favourites…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-neutral-950">
      <div className="scrollbar-hide flex-1 space-y-3 overflow-y-auto px-4 py-4">
        <div className="px-1 pb-1">
          <p className="text-callout font-semibold text-white">Your usual meals</p>
          <p className="mt-1 text-footnote leading-snug text-white/60">
            Log a meal you eat often with one tap. No need to take a photo again.
          </p>
        </div>

        {repeat ? (
          <button
            type="button"
            disabled={busyId != null}
            onClick={() => logMeal(repeat)}
            className="w-full rounded-2xl bg-white px-4 py-3 text-left text-neutral-900 disabled:opacity-60"
          >
            <p className="text-caption1 font-bold">Same {suggested.toLowerCase()} as usual?</p>
            <p className="mt-0.5 text-footnote text-neutral-600">
              Tap to log {repeat.meal_name} for today.
            </p>
          </button>
        ) : null}

        {error ? (
          <p className="text-center text-subhead text-white/70">
            We couldn't open your favourites. Check your connection and try again.
          </p>
        ) : null}

        {!error && meals.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 pt-10 text-center">
            <Heart className="h-10 w-10 text-white/40" />
            <p className="text-callout font-semibold text-white">No favourites yet</p>
            <p className="text-subhead leading-snug text-white/60">
              Take a photo of a meal, tap Add to favourites, then save it. Next time it will be waiting here.
            </p>
          </div>
        ) : null}

        {meals.map((meal) => (
          <div key={meal.favorite_meal_id} className="flex items-center gap-3 rounded-2xl bg-white/10 p-3">
            <button
              type="button"
              disabled={busyId != null}
              onClick={() => logMeal(meal)}
              className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:opacity-60"
            >
              {meal.image_url ? (
                <img src={meal.image_url} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
              ) : (
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/10">
                  <UtensilsCrossed className="h-5 w-5 text-white/70" />
                </span>
              )}
              <span className="min-w-0">
                <span className="block truncate text-callout font-semibold text-white">{meal.meal_name}</span>
                <span className="block truncate text-footnote text-white/60">
                  Tap to log again
                  {meal.meal_type ? ` · ${meal.meal_type}` : ""}
                  {meal.food_names.length > 0 ? ` · ${meal.food_names.join(", ")}` : ""}
                </span>
              </span>
            </button>
            <button
              type="button"
              aria-label="Remove favourite"
              disabled={busyId != null}
              onClick={() => removeMeal(meal)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-red-400 disabled:opacity-60"
            >
              <Heart className="h-5 w-5 fill-current" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FavoriteMealsPanel;
